import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useGiftPopupsListener from "../../../hooks/useGiftPopupsListener";
import { postApiRequest } from "../../../services/postApiRequest";
import { END_POINTS } from "../../../Helper/Constant";
import SendGiftTypeModal from "../SendGiftTypeModal/SendGiftTypeModal";
import GiftPopupEmbers from "./GiftPopupEmbers";
import defaultAvatar from "../../../assets/icons/default.svg";
import "./GiftPopupModal.scss";

const CLOSE_DELAY_MS = 3000;
const MAX_PREVIEW_WORDS = 50;
const isVideoUrl = (url) => /\.(mp4|webm|ogg)(\?|#|$)/i.test(url || "");

const getGiftType = (popup) => popup?.giftType || popup?.gift || {};

const getPopupId = (popup) => {
  const raw =
    popup?.transactionId ||
    popup?._id ||
    popup?.id ||
    popup?.giftId ||
    popup?.popupId ||
    null;
  if (!raw) return null;
  if (typeof raw === "object") return raw._id || raw.id || raw.$oid || null;
  return String(raw);
};

const getGiftName = (giftType, language) => {
  const name = giftType?.name;
  if (!name) return "";
  if (typeof name === "string") return name;
  return language === "ar" ? name.ar || name.en : name.en || name.ar;
};

const getMediaUrl = (popup) => {
  const giftType = getGiftType(popup);
  return giftType.mediaUrl || popup?.mediaUrl || "";
};

const getThumbnailUrl = (popup) => {
  const giftType = getGiftType(popup);
  return giftType.thumbnailUrl || popup?.thumbnailUrl || "";
};

const getSender = (popup) =>
  popup?.sender || popup?.from || popup?.senderUser || popup?.user || {};

const getSenderId = (popup) => {
  const sender = getSender(popup);
  return sender?._id || popup?.senderId || popup?.fromId || null;
};

const getCategory = (popup) =>
  String(
    popup?.category ||
    popup?.giftType?.category ||
    popup?.gift?.category ||
    getGiftType(popup)?.category ||
    ""
  ).toLowerCase();

const getCustomText = (popup) => (popup?.text || "").trim();

const truncateWords = (text, max = MAX_PREVIEW_WORDS) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return "";
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length <= max) return trimmed;
  return `${words.slice(0, max).join(" ")}…`;
};

const GiftPopupModal = () => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const isLogin = useSelector((state) => state.login.isLogin);
  const { popups, dismissCurrent } = useGiftPopupsListener(isLogin);
  const [canClose, setCanClose] = useState(false);
  const [replyCategory, setReplyCategory] = useState(null);
  const [replyReceiverId, setReplyReceiverId] = useState(null);
  const currentPopupRef = useRef(null);

  const currentPopup = popups[0];
  const isOpen = isLogin && popups.length > 0 && !!currentPopup;
  const popupKey = currentPopup?._clientId || getPopupId(currentPopup);
  currentPopupRef.current = currentPopup || null;

  useEffect(() => {
    setCanClose(false);
    if (!isOpen || !popupKey) return undefined;

    const timer = setTimeout(() => {
      setCanClose(true);
    }, CLOSE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isOpen, popupKey]);

  const acknowledgeAndDismiss = useCallback(() => {
    const popup = currentPopupRef.current;
    const transactionId = getPopupId(popup);
    dismissCurrent();

    if (!transactionId) return;
    postApiRequest(`${END_POINTS.ACKNOWLEDGE_GIFT}/${transactionId}`, {}).catch((error) => {
      console.error("Error acknowledging gift popup:", error);
    });
  }, [dismissCurrent]);

  const handleClose = useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (!canClose) return;
      acknowledgeAndDismiss();
    },
    [canClose, acknowledgeAndDismiss]
  );

  const handleSenderClick = useCallback(() => {
    const senderId = getSenderId(currentPopupRef.current);
    if (!senderId) return;
    acknowledgeAndDismiss();
    navigate(`/consumer-Profile/${senderId}`);
  }, [acknowledgeAndDismiss, navigate]);

  const handleReplyBack = useCallback(() => {
    const popup = currentPopupRef.current;
    const senderId = getSenderId(popup);
    const category = getCategory(popup);
    if (!senderId) return;

    acknowledgeAndDismiss();
    if (category === "roast" || category === "hype") {
      setReplyReceiverId(senderId);
      setReplyCategory(category);
      return;
    }
    navigate(`/send-gift/${senderId}`);
  }, [acknowledgeAndDismiss, navigate]);

  const handleCloseReplyModal = useCallback(() => {
    setReplyCategory(null);
    setReplyReceiverId(null);
  }, []);

  const giftType = currentPopup ? getGiftType(currentPopup) : {};
  const giftName = getGiftName(giftType, i18n.language);
  const mediaUrl = getMediaUrl(currentPopup);
  const thumbnailUrl = getThumbnailUrl(currentPopup);
  const mediaSrc = mediaUrl || thumbnailUrl;
  const mediaIsVideo = isVideoUrl(mediaUrl);
  const sender = getSender(currentPopup);
  const senderId = getSenderId(currentPopup);
  const senderName =
    sender?.userName || sender?.firstName || t("user_profile.unknown_user", "Unknown");
  const category = getCategory(currentPopup);
  const customText = truncateWords(getCustomText(currentPopup));
  const isRoast = category === "roast";
  const isHype = category === "hype";
  const replyLabel = isRoast
    ? t("gift.roast_back", "Roast Back")
    : isHype
      ? t("gift.hype_back", "Hype Back")
      : t("gift.gift_back", "Gift Back");
  const challengeText = isRoast
    ? t("gift.roast_challenge", "Are you letting that slide easily?")
    : isHype
      ? t("gift.hype_challenge", "Feeling the hype? Send it back!")
      : t("gift.show_back_love", "show back some love!");

  return (
    <>
      <Modal
        open={isOpen}
        footer={null}
        centered
        closable={false}
        keyboard={false}
        maskClosable={false}
        width={400}
        destroyOnClose
        className={`gift-popup-modal${isRoast || isHype ? " gift-popup-modal--polaroid" : ""}`}
        wrapClassName="gift-popup-modal-wrap"
        zIndex={1200}
      >
        {isOpen && (
          <div
            className={`gift-popup-content gift-popup-content--${category || "gift"}`}
            key={popupKey}
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          >
            <GiftPopupEmbers />
            <span className="gift-popup-glow gift-popup-glow--tl" aria-hidden="true" />
            <span className="gift-popup-glow gift-popup-glow--br" aria-hidden="true" />

            {canClose && (
              <button
                type="button"
                className="gift-popup-close"
                onClick={handleClose}
                aria-label={t("gift.popup_close", "Close")}
              >
                <CloseOutlined />
              </button>
            )}

            <p className="gift-popup-sent">
              {t("gift.sent_you", {
                name: senderName,
                defaultValue: `${senderName} sent you`,
              })}
            </p>
            {giftName ? <h2 className="gift-popup-gift-name">{giftName}</h2> : null}

            <div className="gift-popup-polaroid-stage">
              <span className="gift-popup-polaroid-back gift-popup-polaroid-back--secondary" />
              <span className="gift-popup-polaroid-back gift-popup-polaroid-back--primary" />
              <div className="gift-popup-polaroid">
                {mediaIsVideo ? (
                  <video
                    className="gift-popup-polaroid-media"
                    src={mediaUrl}
                    poster={thumbnailUrl || undefined}
                    muted
                    loop
                    autoPlay
                    playsInline
                  />
                ) : mediaSrc ? (
                  <img className="gift-popup-polaroid-media" src={mediaSrc} alt={giftName} />
                ) : (
                  <div className="gift-popup-polaroid-media gift-popup-polaroid-media--empty" />
                )}
                <div className="gift-popup-polaroid-user">
                  <button
                    type="button"
                    className="gift-popup-polaroid-avatar-btn"
                    onClick={handleSenderClick}
                    disabled={!senderId}
                    aria-label={senderName}
                  >
                    <img
                      className="gift-popup-polaroid-avatar"
                      src={sender?.avatarUrl || defaultAvatar}
                      alt={senderName}
                    />
                  </button>
                  <button
                    type="button"
                    className="gift-popup-polaroid-name"
                    onClick={handleSenderClick}
                    disabled={!senderId}
                  >
                    {senderName}
                  </button>
                </div>
              </div>
            </div>

            {customText ? (
              <p className="gift-popup-message">“{customText}”</p>
            ) : null}

            <p className="gift-popup-prompt">{challengeText}</p>
            <button
              type="button"
              className="gift-popup-reply"
              onClick={handleReplyBack}
              disabled={!senderId}
            >
              {replyLabel}
            </button>
          </div>
        )}
      </Modal>

      <SendGiftTypeModal
        open={Boolean(replyCategory && replyReceiverId)}
        onClose={handleCloseReplyModal}
        category={replyCategory}
        receiverId={replyReceiverId}
      />
    </>
  );
};

export default memo(GiftPopupModal);
